import {LoggerAppender} from '../logger-appender';
import {ConsoleAppenderConfig} from './console-appender-config';
import {LogLevel} from '../../log-level';
import {ConsoleAppenderColors} from './console-appender-colors';
import {LogMessage} from '../../log-message';
import {inject, Injectable} from '../../../di';
import {ConfigService} from '../../../config/config-service';
import {filter, map} from 'rxjs';

const DEFAULT_TRIGGER_LEVEL: LogLevel = LogLevel.Info;
const DEFAULT_CONTEXT_SIZE: number = 0;

/**
 * Console logger appender
 */
@Injectable()
export class ConsoleAppender extends LoggerAppender {
  private readonly configService = inject(ConfigService);

  protected config!: ConsoleAppenderConfig;

  constructor() {
    super();
    this.configService.config$
      .pipe(
        map((c) => c?.logger?.console),
        filter((c) => !!c),
      )
      .subscribe((c) => this.applyConfig(c));
  }

  private applyConfig(config?: ConsoleAppenderConfig) {
    // validation and fixing config:
    if (!config) {
      // Default config
      config = new ConsoleAppenderConfig();
    }
    if (config.triggerLevel == null) {
      config.triggerLevel = DEFAULT_TRIGGER_LEVEL;
    }
    if (config.contextSize == null || config.contextSize <= 0) {
      config.contextSize = DEFAULT_CONTEXT_SIZE;
    }

    // applying config
    this.config = config;
  }

  public log(message: LogMessage, level: LogLevel, args: any[], history: LogMessage[]): void {
    if (!this.config.enabled || level < this.config.triggerLevel) {
      return;
    }

    let logParms: any[];
    if (this.config.objectSerialization === 'string') {
      // message already contains all args converted to string
      if (this.config.timestamp) {
        logParms = [`${new Date().toISOString()} ${message.message}`];
      } else {
        logParms = [message.message];
      }
    } else {
      if (this.config.timestamp) {
        logParms = [new Date().toISOString(), ...args];
      } else {
        logParms = args;
      }
    }

    this.writeToConsole(level, logParms);

    if (this.config.contextSize > 0) {
      this.writeToConsole(level, ['------------------------------']);
      console.log(`Last ${this.config.contextSize} log entries:`);
      history.slice(-this.config.contextSize).forEach((m) => this.writeToConsole(m.level, [m.timestamp, m.message]));
      this.writeToConsole(level, ['------------------------------']);
    }
  }

  private writeToConsole(level: LogLevel, logParms: any[]) {
    // Apply colors
    if (this.config.colors && ConsoleAppenderColors[level]) {
      if (logParms[0] && (typeof logParms[0] === 'string' || (String && logParms[0] instanceof String))) {
        logParms = logParms.slice();
        logParms.splice(1, 0, ConsoleAppenderColors[level]);
        logParms[0] = '%c' + logParms[0];
      }
    }

    if (this.config.nativeLevels) {
      // Apply native log levels
      if ((level === LogLevel.Critical || level === LogLevel.Error) && console.error) {
        console.error(...logParms);
      } else if (level === LogLevel.Warn && console.warn) {
        console.warn(...logParms);
      } else if (level === LogLevel.Debug && console.debug) {
        console.debug(...logParms);
      } else {
        console.log(...logParms);
      }
    } else {
      // Apply default log level for all levels
      console.log(...logParms);
    }
  }
}
